import { useState } from 'react';
import { useSermons } from '../../hooks/useSermons';
import { useSermonSummaries } from '../../hooks/useSermonSummaries';
import { parseISO } from 'date-fns';
import {
  FileText,
  Loader2,
  AlertCircle,
  Quote,
  Download,
  Mail,
  Archive,
  Trash2,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Check,
  Share2,
  Palette,
  Image
} from 'lucide-react';
import { QuoteSocialImageModal } from '../QuoteSocialImageModal';
import { jsPDF } from 'jspdf';
import { createStyledPDF, addSection, cleanAIText } from '../../lib/pdf';
import { supabase, supabaseUrl } from '../../lib/supabase';
import { downloadStudyPageHTML, StudyPageData } from '../../lib/studyPageTemplate';
import { PRESET_THEMES, loadSelectedTheme, loadCustomColors, type StudyPageThemeId } from '../../lib/studyPageThemes';


export function SermonSummarizer({ compact, defaultSermonId }: { compact?: boolean; defaultSermonId?: string } = {}) {
  const { sermons } = useSermons();
  const {
    summaries,
    loading,
    error,
    showArchived,
    setShowArchived,
    saveSummary,
    toggleArchive,
    deleteSummary
  } = useSermonSummaries();

  const [selectedSermonId, setSelectedSermonId] = useState(defaultSermonId ?? '');
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [expandedSummaries, setExpandedSummaries] = useState<Set<string>>(new Set());
  const [sharingId, setSharingId] = useState<string | null>(null);
  const [copiedShareId, setCopiedShareId] = useState<string | null>(null);
  const [themePicker, setThemePicker] = useState<string | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<StudyPageThemeId>(loadSelectedTheme());
  const [socialPostQuote, setSocialPostQuote] = useState<string | null>(null);
  const [socialPostTitle, setSocialPostTitle] = useState<string | undefined>(undefined);

  const toggleExpanded = (id: string) => {
    setExpandedSummaries(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const generateSummary = async () => {
    if (!selectedSermonId) return;

    try {
      setGenerating(true);
      setGenerationError(null);

      const sermon = sermons.find(s => s.id === selectedSermonId);
      if (!sermon) throw new Error('Sermon not found');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No authentication session');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          type: 'summary',
          content: sermon.content
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate summary');
      }

      const summaryResult = await response.json();

      if (!summaryResult.success) {
        throw new Error(summaryResult.error || 'Failed to generate summary');
      }

      let bulletinSummary = '';
      let quotes: string[] = [];
      let metadata: {
        mainPoints?: string[];
        keyScriptures?: string[];
        practicalApplications?: string[];
        prayerPoints?: string[];
        discussionQuestions?: string[];
        closingReflection?: string;
      } = {};

      try {
        const parsed = JSON.parse(summaryResult.content);
        bulletinSummary = parsed.bulletinSummary || '';
        quotes = parsed.quotes || [];
        metadata = {
          mainPoints: parsed.mainPoints || [],
          keyScriptures: parsed.keyScriptures || [],
          practicalApplications: parsed.practicalApplications || [],
          prayerPoints: parsed.prayerPoints || [],
          discussionQuestions: parsed.discussionQuestions || [],
          closingReflection: parsed.closingReflection || ''
        };
        console.log('Parsed comprehensive summary:', { bulletinSummary, quotes, metadata });
      } catch {
        bulletinSummary = summaryResult.content;
        quotes = [
          "God's love transforms our hearts and lives.",
          "Through faith, we find strength in challenging times.", 
          "Let us live out the Gospel in our daily actions.",
          "Prayer is our direct line to God's wisdom and peace."
        ];
        console.log('Using fallback format - could not parse JSON response');
      }

      await saveSummary(selectedSermonId, {
        bulletin_summary: bulletinSummary,
        quotes: quotes,
        metadata: metadata
      });
      setSelectedSermonId('');
    } catch (error) {
      console.error('Error generating summary:', error);
      setGenerationError(error instanceof Error ? error.message : 'Failed to generate summary');
    } finally {
      setGenerating(false);
    }
  };

  const downloadPDF = (summary: typeof summaries[0]) => {
    const sermon = sermons.find(s => s.id === summary.sermon_id);
    if (!sermon) return;

    const doc = new jsPDF();
    let yPos = createStyledPDF({
      title: 'Sermon Summary',
      subtitle: sermon.title,
      date: sermon.date ? parseISO(sermon.date).toLocaleDateString() : undefined
    }, doc);

    if (summary.bulletin_summary) {
      yPos = addSection(doc, 'Bulletin Summary', cleanAIText(summary.bulletin_summary), yPos);
    }

    if (summary.quotes?.length) {
      const cleanedQuotes = summary.quotes.map(quote => `"${cleanAIText(quote)}"`);
      yPos = addSection(doc, 'Social Media Quotes', cleanedQuotes, yPos);
    }

    doc.save(`sermon-summary-${sermon.title.toLowerCase().replace(/\s+/g, '-')}.pdf`);
  };

  const getStudyPageData = (summary: typeof summaries[0]): StudyPageData | null => {
    const sermon = sermons.find(s => s.id === summary.sermon_id);
    if (!sermon) return null;

    const metadata = summary.metadata || {};

    return {
      title: sermon.title,
      scripture: sermon.scripture || '',
      date: sermon.date ? parseISO(sermon.date).toLocaleDateString('en-US', {
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }) : undefined,
      bulletinSummary: summary.bulletin_summary || '',
      quotes: summary.quotes || [],
      mainPoints: metadata.mainPoints || [],
      keyScriptures: metadata.keyScriptures || [],
      practicalApplications: metadata.practicalApplications || [],
      prayerPoints: metadata.prayerPoints || [],
      discussionQuestions: metadata.discussionQuestions || [],
      youtubeUrl: sermon.youtube_url || undefined
    };
  };

  const handleDownloadStudyPage = (summary: typeof summaries[0]) => {
    const data = getStudyPageData(summary);
    if (data) {
      downloadStudyPageHTML(data);
    }
  };

  const shareViaEmail = (summary: typeof summaries[0]) => {
    const sermon = sermons.find(s => s.id === summary.sermon_id);
    if (!sermon) return;

    let emailBody = `Sermon Summary for "${sermon.title}"\n\n`;
    if (sermon.scripture) {
      emailBody += `Scripture: ${sermon.scripture}\n\n`;
    }

    if (summary.bulletin_summary) {
      emailBody += 'Bulletin Summary:\n';
      emailBody += summary.bulletin_summary + '\n\n';
    }

    if (summary.quotes?.length) {
      emailBody += 'Social Media Quotes:\n';
      summary.quotes.forEach(quote => {
        emailBody += `"${quote}"\n`;
      });
    }

    const subject = encodeURIComponent(`Sermon Summary for ${sermon.title}`);
    const body = encodeURIComponent(emailBody);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const generateShareId = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let result = '';
    for (let i = 0; i < 10; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const createShareableLink = async (summary: typeof summaries[0], themeOverride?: StudyPageThemeId) => {
    const sermon = sermons.find(s => s.id === summary.sermon_id);
    if (!sermon) return;

    setSharingId(summary.id);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const metadata = summary.metadata || {};
      const themeToUse = themeOverride || selectedTheme;
      
      let shareId = generateShareId();
      let attempts = 0;
      const maxAttempts = 3;
      
      let includeDiscussionQuestions = true;
      let includeClosingReflection = true;
      let includeTheme = true;
      
      while (attempts < maxAttempts) {
        const insertData: Record<string, any> = {
            share_id: shareId,
            user_id: user.id,
            sermon_id: sermon.id,
            title: sermon.title,
            scripture: sermon.scripture || null,
            sermon_date: sermon.date || null,
            bulletin_summary: summary.bulletin_summary || null,
            quotes: summary.quotes || [],
            main_points: metadata.mainPoints || [],
            key_scriptures: metadata.keyScriptures || [],
            practical_applications: metadata.practicalApplications || [],
            prayer_points: metadata.prayerPoints || [],
            youtube_url: sermon.youtube_url || null
        };

        if (includeTheme) {
          insertData.theme = themeToUse;
          if (themeToUse === 'custom') {
            insertData.custom_theme_colors = loadCustomColors();
          }
        }

        if (includeDiscussionQuestions && metadata.discussionQuestions?.length) {
          insertData.discussion_questions = metadata.discussionQuestions;
        }

        if (includeClosingReflection && metadata.closingReflection) {
          insertData.closing_reflection = metadata.closingReflection;
        }

        const { error: insertError } = await supabase
          .from('shared_study_pages')
          .insert([insertData]);

        if (!insertError) {
          const directUrl = `${window.location.origin}/study/${shareId}`;
          let socialShareUrl = directUrl;
          
          if (supabaseUrl) {
            socialShareUrl = `${supabaseUrl}/functions/v1/study-page-meta?id=${shareId}`;
          }
          
          console.log('Share URLs:', { supabaseUrl, socialShareUrl, directUrl });
          
          try {
            await navigator.clipboard.writeText(socialShareUrl);
          } catch {
            console.warn('Clipboard access denied, link still created');
          }
          window.open(directUrl, '_blank');
          setCopiedShareId(summary.id);
          setTimeout(() => setCopiedShareId(null), 3000);
          return;
        }
        
        if (insertError.code === '23505') {
          shareId = generateShareId();
          attempts++;
        } else if (insertError.message?.includes('theme') || insertError.message?.includes('custom_theme_colors')) {
          includeTheme = false;
          console.warn('theme columns not found, retrying without them');
        } else if (insertError.message?.includes('closing_reflection')) {
          includeClosingReflection = false;
          console.warn('closing_reflection column not found, retrying without it');
        } else if ((insertError.code === 'PGRST204' || insertError.message?.includes('discussion_questions')) && includeDiscussionQuestions) {
          includeDiscussionQuestions = false;
          console.warn('discussion_questions column not found, retrying without it');
        } else {
          throw insertError;
        }
      }
      
      throw new Error('Failed to generate unique share ID after multiple attempts');
    } catch (err) {
      console.error('Error creating shareable link:', err);
      alert('Failed to create shareable link. Please try again.');
    } finally {
      setSharingId(null);
    }
  };

  return (
    <div className={compact ? 'space-y-3' : 'space-y-6'}>
      <div className="flex justify-between items-center mb-4">
        <h2 className={`font-semibold text-midnight ${compact ? 'text-sm' : 'text-xl'}`}>Sermon Study Guide</h2>
        {!compact && (
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`flex items-center px-4 py-2 rounded-lg ${
              showArchived
                ? 'bg-slate/20 text-slate hover:bg-slate/30'
                : 'bg-sand/20 text-slate hover:bg-sand/30'
            }`}
          >
            {showArchived ? (
              <>
                <RotateCcw className="h-5 w-5 mr-2" />
                Show Active
              </>
            ) : (
              <>
                <Archive className="h-5 w-5 mr-2" />
                Show Archived
              </>
            )}
          </button>
        )}
      </div>

      <div className={`bg-white rounded-lg shadow-sm border border-sand/20 ${compact ? 'p-3' : 'p-6'}`}>
        <h3 className={`font-semibold text-midnight mb-4 ${compact ? 'text-sm' : 'text-lg'}`}>Generate New Summary</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate mb-2">
              Select Sermon
            </label>
            <select
              value={selectedSermonId}
              onChange={e => setSelectedSermonId(e.target.value)}
              className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
            >
              <option value="">Choose a sermon...</option>
              {sermons.map(sermon => (
                <option key={sermon.id} value={sermon.id}>
                  {sermon.title}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={generateSummary}
            disabled={!selectedSermonId || generating}
            className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 flex items-center"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating Summary...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Generate Summary
              </>
            )}
          </button>
        </div>
      </div>

      {generationError && (
        <div className="bg-crimson/10 text-crimson p-4 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {generationError}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-electric" />
        </div>
      ) : error ? (
        <div className="bg-crimson/10 text-crimson p-4 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      ) : summaries.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-sand/20">
          <FileText className="h-12 w-12 text-slate/50 mx-auto mb-4" />
          <p className="text-slate">
            {showArchived 
              ? 'No archived summaries found'
              : 'No summaries available. Generate a summary to get started!'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {summaries.map(summary => {
            const sermon = sermons.find(s => s.id === summary.sermon_id);
            if (!sermon) return null;

            return (
              <div
                key={summary.id}
                className="bg-white rounded-lg shadow-sm border border-sand/20 overflow-hidden"
              >
                <div
                  className={`${compact ? 'p-3' : 'p-6'} cursor-pointer hover:bg-sand/5`}
                  onClick={() => toggleExpanded(summary.id)}
                >
                  {compact ? (
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-xs font-medium text-midnight leading-tight">{sermon.title}</h3>
                        {expandedSummaries.has(summary.id) ? (
                          <ChevronUp className="h-4 w-4 text-slate flex-shrink-0 mt-0.5" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-slate flex-shrink-0 mt-0.5" />
                        )}
                      </div>
                      <div className="text-[10px] text-slate/60">{new Date(summary.created_at).toLocaleDateString()}</div>
                      <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => { e.stopPropagation(); createShareableLink(summary); }}
                          disabled={sharingId === summary.id}
                          className="p-1.5 text-slate hover:text-emerald rounded"
                          title={copiedShareId === summary.id ? 'Link copied!' : 'Share Study Page'}
                        >
                          {sharingId === summary.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : copiedShareId === summary.id ? (
                            <Check className="h-3.5 w-3.5 text-emerald" />
                          ) : (
                            <Share2 className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadStudyPage(summary); }}
                          className="p-1.5 text-slate hover:text-electric rounded"
                          title="Download Study Page"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); downloadPDF(summary); }}
                          className="p-1.5 text-slate hover:text-electric rounded"
                          title="Download PDF"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this summary?')) deleteSummary(summary.id); }}
                          className="p-1.5 text-slate hover:text-crimson rounded"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-midnight">{sermon.title}</h3>
                        <div className="text-sm text-slate">
                          {new Date(summary.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 relative">
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setThemePicker(themePicker === summary.id ? null : summary.id);
                            }}
                            className="p-2 text-slate hover:text-electric rounded-lg"
                            title="Choose theme"
                          >
                            <Palette className="h-5 w-5" />
                          </button>
                          {themePicker === summary.id && (
                            <div className="absolute right-0 bottom-full mb-1 z-50 bg-white border border-sand/30 rounded-xl shadow-lg p-3 min-w-[220px]" onClick={(e) => e.stopPropagation()}>
                              <p className="text-xs font-semibold text-slate mb-2 uppercase tracking-wider">Study Page Theme</p>
                              <div className="space-y-1">
                                {PRESET_THEMES.map((theme) => (
                                  <button
                                    key={theme.id}
                                    onClick={() => {
                                      setSelectedTheme(theme.id);
                                      setThemePicker(null);
                                      createShareableLink(summary, theme.id);
                                    }}
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                                      selectedTheme === theme.id ? 'bg-electric/10 text-electric' : 'hover:bg-sand/10 text-midnight'
                                    }`}
                                  >
                                    <div className="flex gap-0.5">
                                      {theme.id === 'dark' && (
                                        <>
                                          <span className="w-3 h-3 rounded-full bg-[#1a2218] border border-gray-300" />
                                          <span className="w-3 h-3 rounded-full bg-[#8a9a5b]" />
                                          <span className="w-3 h-3 rounded-full bg-[#f0ead6]" />
                                        </>
                                      )}
                                      {theme.id === 'light' && (
                                        <>
                                          <span className="w-3 h-3 rounded-full bg-[#faf8f4] border border-gray-300" />
                                          <span className="w-3 h-3 rounded-full bg-[#2b6cb0]" />
                                          <span className="w-3 h-3 rounded-full bg-[#1a202c]" />
                                        </>
                                      )}
                                      {theme.id === 'custom' && (
                                        <>
                                          <span className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-orange-400" />
                                          <span className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-green-400" />
                                          <span className="w-3 h-3 rounded-full bg-gradient-to-r from-red-500 to-yellow-400" />
                                        </>
                                      )}
                                    </div>
                                    <div>
                                      <div className="text-sm font-medium">{theme.name}</div>
                                      <div className="text-xs text-slate">{theme.description}</div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                              <p className="text-[10px] text-slate/60 mt-2 pt-2 border-t border-sand/20">Custom colors can be set in Settings → Sermons</p>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            createShareableLink(summary);
                          }}
                          disabled={sharingId === summary.id}
                          className="p-2 text-slate hover:text-emerald rounded-lg disabled:opacity-50"
                          title={copiedShareId === summary.id ? "Link copied & opened!" : "Share Study Page"}
                        >
                          {sharingId === summary.id ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : copiedShareId === summary.id ? (
                            <Check className="h-5 w-5 text-emerald" />
                          ) : (
                            <Share2 className="h-5 w-5" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadStudyPage(summary);
                          }}
                          className="p-2 text-slate hover:text-electric rounded-lg"
                          title="Download Template Study Page"
                        >
                          <ExternalLink className="h-5 w-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadPDF(summary);
                          }}
                          className="p-2 text-slate hover:text-electric rounded-lg"
                          title="Download PDF"
                        >
                          <Download className="h-5 w-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            shareViaEmail(summary);
                          }}
                          className="p-2 text-slate hover:text-electric rounded-lg"
                          title="Share via Email"
                        >
                          <Mail className="h-5 w-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleArchive(summary.id, summary.archived);
                          }}
                          className="p-2 text-slate hover:text-electric rounded-lg"
                          title={summary.archived ? 'Unarchive' : 'Archive'}
                        >
                          {summary.archived ? (
                            <RotateCcw className="h-5 w-5" />
                          ) : (
                            <Archive className="h-5 w-5" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Are you sure you want to delete this summary?')) {
                              deleteSummary(summary.id);
                            }
                          }}
                          className="p-2 text-slate hover:text-crimson rounded-lg"
                          title="Delete"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                        {expandedSummaries.has(summary.id) ? (
                          <ChevronUp className="h-5 w-5 text-slate" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate" />
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {expandedSummaries.has(summary.id) && (
                  <div className="px-6 pb-6 border-t border-sand/20 pt-4">
                    {summary.bulletin_summary && (
                      <div className="mb-6">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="h-5 w-5 text-electric" />
                          <h4 className="font-medium text-midnight">Bulletin Summary</h4>
                        </div>
                        <p className="text-slate whitespace-pre-wrap">{summary.bulletin_summary}</p>
                      </div>
                    )}

                    {summary.quotes?.length > 0 && (
                      <div className="mb-6">
                        <div className="flex items-center gap-2 mb-2">
                          <Quote className="h-5 w-5 text-electric" />
                          <h4 className="font-medium text-midnight">Social Media Quotes</h4>
                        </div>
                        <ul className="space-y-4">
                          {summary.quotes.map((quote, index) => (
                            <li key={index} className="flex items-start gap-3 p-4 bg-sand/10 rounded-lg">
                              <span className="text-slate italic flex-1">"{quote}"</span>
                              <button
                                onClick={() => {
                                  const sermon = sermons.find(s => s.id === summary.sermon_id);
                                  setSocialPostQuote(quote);
                                  setSocialPostTitle(sermon?.title);
                                }}
                                className="flex-shrink-0 p-1.5 text-slate hover:text-electric rounded-md transition-colors"
                                title="Create social image"
                              >
                                <Image className="h-4 w-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {socialPostQuote && (
        <QuoteSocialImageModal
          quote={socialPostQuote}
          sermonTitle={socialPostTitle}
          onClose={() => { setSocialPostQuote(null); setSocialPostTitle(undefined); }}
        />
      )}
    </div>
  );
}